'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'motion/react';
import type { ReactNode } from 'react';
import { MotionSection } from '@/components/ui/motion-section';
import { SiteHeader } from '@/components/marketing/site-header';
import { SiteFooter } from '@/components/marketing/site-footer';

export default function LandingPage() {
  const features: { title: string; desc: string; icon: ReactNode; href?: string; linkLabel?: string }[] = [
    {
      title: 'Payment processing',
      desc: 'Donations collected bank-to-bank, one-off or regular — lower fees than cards, and regular gifts don’t stop when a card expires.',
      href: '/why-direct-debit',
      linkLabel: 'Why Direct Debit?',
      icon: (
        <svg className="w-6 h-6 text-trust-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
    },
    {
      title: 'Donor management',
      desc: 'Brevo CRM auto-connected on signup. Donor contact list syncs and a Gift Aid declaration email template is provisioned automatically — branded, edit-anywhere, ready to send.',
      icon: (
        <svg className="w-6 h-6 text-trust-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      title: 'White-label options',
      desc: 'Custom domains and iframe embedding. Your branding, your donor experience.',
      icon: (
        <svg className="w-6 h-6 text-trust-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
      ),
    },
  ];

  return (
    <div className="w-full min-h-screen bg-warm-gray-50 text-warm-gray-700 leading-relaxed">
      <SiteHeader />

      <main>
        {/* Hero */}
        <section className="relative min-h-[90vh] pt-32 pb-24 overflow-hidden">
          {/* Background layers */}
          <div className="absolute inset-0 bg-warm-gray-50" />
          <div className="absolute inset-0 bg-mesh-hero" />
          <div className="absolute inset-0 bg-grid-subtle opacity-50" />

          {/* Decorative orbs */}
          <div className="absolute top-20 right-[10%] w-[500px] h-[500px] rounded-full bg-trust-blue/5 blur-3xl pointer-events-none" />
          <div className="absolute bottom-20 left-[5%] w-[400px] h-[400px] rounded-full bg-growth-green/5 blur-3xl pointer-events-none" />

          <div className="max-w-[1400px] mx-auto px-6 relative z-10">
            <div className="grid lg:grid-cols-[1.2fr_1fr] gap-16 items-center">
              {/* Left content */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* Badge */}
                <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/80 backdrop-blur-xs border border-trust-blue/20 rounded-full shadow-premium mb-8">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-growth-green opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-growth-green"></span>
                  </span>
                  <span className="text-sm font-medium text-navy">Now live — accepting charities</span>
                </div>

                {/* Headline */}
                <h1 className="font-display text-[clamp(2.75rem,5.5vw,4.25rem)] font-medium text-navy leading-[1.1] tracking-tight mb-8">
                  <span className="block">Donation tools</span>
                  <span className="block mt-2 text-gradient">
                    built by charity trustees
                  </span>
                </h1>

                {/* Subhead */}
                <p className="font-serif text-xl text-warm-gray-600 max-w-[520px] mb-10 leading-relaxed">
                  Gift Aid automation and donation processing for small UK charities.
                  Simple, affordable tools from someone who actually runs a charity.
                </p>

                {/* CTAs */}
                <div className="flex flex-wrap gap-4 items-center">
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Link
                      href="/signup"
                      className="group relative inline-flex items-center justify-center px-8 py-4 text-base font-semibold rounded-xl bg-navy text-white overflow-hidden press-effect"
                    >
                      <span className="relative z-10">Get started free</span>
                      <div className="absolute inset-0 bg-linear-to-r from-trust-blue to-growth-green opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </Link>
                  </motion.div>

                  <a href="#features" className="inline-flex items-center gap-2 px-6 py-4 text-base font-medium text-navy hover:text-trust-blue transition-colors group">
                    See features
                    <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </a>
                </div>
              </motion.div>

              {/* Right side - visual element */}
              <motion.div
                className="hidden lg:block relative"
                initial={{ opacity: 0, scale: 0.9, x: 40 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className="relative h-[480px]">
                  {/* Card 1 - tilted back */}
                  <div className="absolute top-4 left-4 w-[260px] p-6 bg-white rounded-2xl shadow-premium-lg -rotate-6 border border-warm-gray-200">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-trust-blue/10 flex items-center justify-center">
                        <svg className="w-5 h-5 text-trust-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="font-semibold text-navy text-sm">Gift Aid Claim</span>
                    </div>
                    <div className="text-3xl font-display font-medium text-navy">£2,847.50</div>
                    <div className="text-sm text-growth-green mt-1 font-medium">Submitted to HMRC</div>
                  </div>

                  {/* Card 2 - main focus */}
                  <div className="absolute top-20 left-20 w-[280px] p-6 bg-white rounded-2xl shadow-premium-lg border border-warm-gray-200 z-10">
                    <div className="text-xs font-semibold uppercase tracking-wider text-warm-gray-500 mb-3">This Month</div>
                    <div className="text-4xl font-display font-medium text-navy mb-2">47</div>
                    <div className="text-warm-gray-600">donations received</div>
                    <div className="mt-4 h-16 bg-linear-to-r from-trust-blue/10 via-trust-blue/20 to-growth-green/10 rounded-lg flex items-end px-2 pb-2 gap-1">
                      {[40, 65, 45, 80, 55, 70, 90].map((h, i) => (
                        <div key={i} className="flex-1 bg-trust-blue/60 rounded-xs" style={{ height: `${h}%` }} />
                      ))}
                    </div>
                  </div>

                  {/* Card 3 - tilted forward */}
                  <div className="absolute bottom-4 right-0 w-[240px] p-5 bg-linear-to-br from-navy to-navy-light rounded-2xl shadow-premium-lg rotate-[4deg] text-white">
                    <div className="text-xs font-semibold uppercase tracking-wider text-white/60 mb-2">Annual Total</div>
                    <div className="text-3xl font-display font-medium">£18,420</div>
                    <div className="text-sm text-growth-green-300 mt-1 font-medium">+23% vs last year</div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Trust indicators */}
            <motion.div
              className="mt-20 pt-10 border-t border-warm-gray-200"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex flex-wrap gap-8 lg:gap-12 items-center justify-center lg:justify-start">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-growth-green/10 flex items-center justify-center">
                    <svg className="w-5 h-5 text-growth-green" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-semibold text-navy text-sm">HMRC Recognised</div>
                    <div className="text-xs text-warm-gray-500">Claims direct via Government Gateway</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-gold/10 flex items-center justify-center">
                    <span className="text-lg font-bold text-accent-gold" aria-hidden="true">£</span>
                  </div>
                  <div>
                    <div className="font-semibold text-navy text-sm">No Monthly Fees</div>
                    <div className="text-xs text-warm-gray-500">Pay only when you receive</div>
                  </div>
                </div>
                <Link href="/why-direct-debit" className="flex items-center gap-3 no-underline group/badge rounded-lg -m-1 p-1 transition-colors hover:bg-white/60">
                  <Image src="/gocardless-symbol.svg" alt="GoCardless" width={40} height={40} className="w-10 h-10 rounded-full" />
                  <div>
                    <div className="font-semibold text-navy text-sm group-hover/badge:text-trust-blue transition-colors">GoCardless Partner</div>
                    <div className="text-xs text-warm-gray-500">
                      Open Banking & Direct Debit ·{' '}
                      <span className="text-trust-blue-700 font-semibold underline underline-offset-2 group-hover/badge:text-trust-blue-800">Why? &rarr;</span>
                    </div>
                  </div>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Problem */}
        <section className="py-32 bg-white relative overflow-hidden">
          <div className="absolute inset-0 bg-dots opacity-30" />

          <div className="max-w-[1400px] mx-auto px-6 relative">
            <MotionSection>
              <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-trust-blue mb-6">
                <span className="w-8 h-px bg-trust-blue"></span>
                The problem
              </span>
              <h2 className="font-display text-[clamp(2rem,4vw,3rem)] font-medium text-navy leading-[1.15] tracking-tight max-w-[700px] mb-16">
                Millions in Gift Aid go unclaimed every year &mdash; and it&apos;s not hard to see why
              </h2>
            </MotionSection>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { num: '01', title: 'A process built for specialists', desc: 'Declarations, spreadsheets, HMRC schemas, Government Gateway. It’s a lot to ask of a volunteer with an evening a week.' },
                { num: '02', title: 'Fixed costs don\u2019t scale down', desc: 'Existing tools start at £30\u201340/month. For a charity raising £2,000/year, that wipes out the Gift Aid benefit.' },
                { num: '03', title: 'Built for big charities', desc: 'Most platforms assume dedicated admin staff, technical confidence, and time that volunteer trustees don\u2019t have.' },
              ].map((item, i) => (
                <MotionSection key={i} delay={i * 0.1}>
                  <div className="h-full p-8 bg-warm-gray-50 rounded-2xl border border-warm-gray-200 hover-lift card-glow">
                    <span className="font-display text-4xl font-medium text-warm-gray-300 mb-4 block">{item.num}</span>
                    <h3 className="font-semibold text-navy text-lg mb-3">{item.title}</h3>
                    <p className="text-warm-gray-600 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </MotionSection>
              ))}

              {/* Solution card */}
              <MotionSection delay={0.3}>
                <div className="h-full p-8 bg-linear-to-br from-navy to-navy-light rounded-2xl text-white relative overflow-hidden hover-lift">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-trust-blue/20 rounded-full blur-2xl" />
                  <span className="font-display text-4xl font-medium text-white/30 mb-4 block">04</span>
                  <h3 className="font-semibold text-white text-lg mb-3 relative z-10">TrusteeKit is different</h3>
                  <p className="text-white/80 text-sm leading-relaxed relative z-10">
                    No fixed costs. Pay only when you claim Gift Aid. Designed for a trustee fitting this in around a day job.
                  </p>
                  <div className="absolute bottom-4 right-4 w-12 h-12 rounded-full bg-growth-green/20 flex items-center justify-center">
                    <svg className="w-6 h-6 text-growth-green" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              </MotionSection>
            </div>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="py-32 bg-warm-gray-50 relative">
          <div className="absolute inset-0 bg-mesh-section" />

          <div className="max-w-[1400px] mx-auto px-6 relative z-10">
            <div className="text-center mb-20">
              <MotionSection>
                <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-trust-blue mb-6 justify-center">
                  <span className="w-8 h-px bg-trust-blue"></span>
                  Features
                  <span className="w-8 h-px bg-trust-blue"></span>
                </span>
                <h2 className="font-display text-[clamp(2rem,4vw,3rem)] font-medium text-navy leading-tight tracking-tight mb-6">
                  Everything you need, nothing you don&apos;t
                </h2>
                <p className="text-warm-gray-600 max-w-[560px] mx-auto font-serif text-lg">
                  Core functionality for small charities. No complex multi-user
                  permissions, no mandatory training, no vendor lock-in.
                </p>
              </MotionSection>
            </div>

            {/* Bento Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Featured item - spans 2 columns */}
              <MotionSection className="lg:col-span-2">
                <div className="h-full p-10 bg-white rounded-3xl border border-warm-gray-200 shadow-premium hover-lift card-glow group">
                  <div className="flex flex-col lg:flex-row gap-8 items-start">
                    <div className="flex-1">
                      <div className="w-14 h-14 flex items-center justify-center bg-linear-to-br from-trust-blue to-trust-blue-600 rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-navy mb-3">Gift Aid Automation</h3>
                      <p className="text-warm-gray-600 leading-relaxed mb-6">
                        Collect declarations, match them to donations, and send your
                        Gift Aid claim straight to HMRC — automatically. No spreadsheets,
                        no re-keying, nothing manual.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-trust-blue/10 text-trust-blue text-xs font-medium rounded-full">Automated HMRC submission</span>
                        <span className="px-3 py-1 bg-trust-blue/10 text-trust-blue text-xs font-medium rounded-full">Declaration forms</span>
                        <span className="px-3 py-1 bg-trust-blue/10 text-trust-blue text-xs font-medium rounded-full">Claim tracking</span>
                      </div>
                    </div>
                    <div className="w-full lg:w-56 h-40 bg-linear-to-br from-trust-blue/5 to-growth-green/5 rounded-2xl border border-warm-gray-100 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-3xl font-display font-medium text-navy">25%</div>
                        <div className="text-sm text-warm-gray-500">more for your charity</div>
                      </div>
                    </div>
                  </div>
                </div>
              </MotionSection>

              {/* Standard items — last one spans 2 cols at lg to keep the
                  bento grid hole-free with the current feature count. */}
              {features.map((feature, i) => (
                <MotionSection
                  key={i}
                  delay={0.1 * (i + 1)}
                  className={i === features.length - 1 ? 'lg:col-span-2' : ''}
                >
                  <div className="h-full p-8 bg-white rounded-2xl border border-warm-gray-200 shadow-premium hover-lift card-glow group">
                    <div className="w-12 h-12 flex items-center justify-center bg-linear-to-br from-trust-blue/10 to-growth-green/10 rounded-xl mb-5 group-hover:scale-110 transition-transform">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-navy mb-3">{feature.title}</h3>
                    <p className="text-warm-gray-600 text-sm leading-relaxed">{feature.desc}</p>
                    {feature.href && (
                      <Link href={feature.href} className="inline-flex items-center gap-1.5 mt-4 text-trust-blue-700 hover:text-trust-blue-800 font-medium text-sm transition-colors">
                        {feature.linkLabel}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </Link>
                    )}
                  </div>
                </MotionSection>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="py-32 bg-white relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-radial from-trust-blue/5 via-transparent to-transparent rounded-full" />

          <div className="max-w-[1400px] mx-auto px-6 relative z-10">
            <div className="text-center mb-16">
              <MotionSection>
                <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-trust-blue mb-6 justify-center">
                  <span className="w-8 h-px bg-trust-blue"></span>
                  Pricing
                  <span className="w-8 h-px bg-trust-blue"></span>
                </span>
                <h2 className="font-display text-[clamp(2rem,4vw,3rem)] font-medium text-navy leading-tight tracking-tight mb-6">
                  Pricing that starts at zero
                </h2>
                <p className="text-warm-gray-600 max-w-[520px] mx-auto font-serif text-lg">
                  Zero upfront costs. Pay only when you claim Gift Aid.
                </p>
              </MotionSection>
            </div>

            {/* Pricing cards */}
            <div className="flex flex-col lg:flex-row gap-8 justify-center items-stretch max-w-[900px] mx-auto">
              <MotionSection delay={0.1} className="flex-1">
                <div className="h-full p-10 bg-linear-to-br from-warm-gray-900 to-warm-gray-800 rounded-3xl text-white shadow-premium-lg hover-lift">
                  <div className="text-sm font-bold text-warm-gray-500 uppercase tracking-wider mb-6">Payment Processing</div>
                  <div className="flex items-baseline gap-1 mb-3">
                    <span className="font-display text-6xl font-medium">0</span>
                    <span className="text-2xl font-semibold text-warm-gray-500">%</span>
                  </div>
                  <div className="text-lg text-warm-gray-500 mb-8">platform fee</div>
                  <div className="pt-6 border-t border-white/10">
                    <p className="text-warm-gray-300 text-sm leading-relaxed">
                      Payments via Open Banking or Direct Debit at discounted rates. No TrusteeKit markup.{' '}
                      <Link href="/why-direct-debit" className="text-white underline underline-offset-2 hover:text-warm-gray-200 transition-colors whitespace-nowrap">
                        Why Direct Debit? &rarr;
                      </Link>
                    </p>
                  </div>
                </div>
              </MotionSection>

              <MotionSection delay={0.2} className="flex-1">
                <div className="h-full p-10 bg-linear-to-br from-trust-blue to-trust-blue-700 rounded-3xl text-white shadow-premium-lg relative overflow-hidden hover-lift">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
                  <div className="relative z-10">
                    <div className="inline-flex px-3 py-1 bg-white/20 rounded-full text-xs font-bold uppercase tracking-wider mb-6">
                      Only pay on success
                    </div>
                    <div className="text-sm font-bold text-white/70 uppercase tracking-wider mb-6">Gift Aid Fee</div>
                    <div className="flex items-baseline gap-1 mb-3">
                      <span className="font-display text-6xl font-medium">1.25</span>
                      <span className="text-2xl font-semibold text-white/70">%</span>
                    </div>
                    <div className="text-lg text-white/70 mb-8">
                      of donation value <span className="text-white/50">(= 5% of Gift Aid claimed)</span>
                    </div>
                    <div className="pt-6 border-t border-white/20">
                      <p className="text-white/80 text-sm leading-relaxed">
                        Applies when you claim, however the donation arrived — deducted at
                        source for online donations, collected by Direct Debit about a month
                        after your claim for the rest. Capped at £25 per donation.
                      </p>
                    </div>
                  </div>
                </div>
              </MotionSection>
            </div>

            {/* Value props */}
            <MotionSection delay={0.4} className="mt-12">
              <div className="flex flex-wrap justify-center gap-8">
                {['No monthly subscription', 'No setup costs', 'No minimum spend'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-growth-green/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-growth-green" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-warm-gray-600 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </MotionSection>

          </div>
        </section>

        {/* About */}
        <section id="about" className="py-32 bg-warm-gray-50 relative overflow-hidden">
          <div className="absolute inset-0 bg-mesh-section" />

          <div className="max-w-[1400px] mx-auto px-6 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
              {/* Quote card */}
              <MotionSection className="order-2 lg:order-1 relative">
                <div className="relative">
                  {/* Decorative quote mark */}
                  <div className="absolute -top-12 -left-4 font-display text-[180px] leading-none text-trust-blue/10 select-none pointer-events-none">
                    &ldquo;
                  </div>

                  <div className="relative z-10 p-12 bg-white rounded-3xl shadow-premium-lg border border-warm-gray-200">
                    <p className="font-display text-2xl md:text-3xl font-normal text-navy leading-relaxed mb-8">
                      I built TrusteeKit because too many small charities leave
                      Gift Aid on the table — not because they don&apos;t qualify,
                      but because the process assumes resources they don&apos;t have.
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-linear-to-br from-trust-blue to-growth-green rounded-full flex items-center justify-center text-white font-display text-xl">
                        A
                      </div>
                      <div>
                        <div className="font-semibold text-navy text-lg">Adam</div>
                        <div className="text-warm-gray-500">Founder & Charity Trustee</div>
                      </div>
                    </div>
                  </div>
                </div>
              </MotionSection>

              {/* Content */}
              <MotionSection className="order-1 lg:order-2" delay={0.2}>
                <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-trust-blue mb-6">
                  <span className="w-8 h-px bg-trust-blue"></span>
                  About
                </span>
                <h2 className="font-display text-[clamp(2rem,3.5vw,2.75rem)] font-medium text-navy leading-snug tracking-tight mb-8">
                  Built by a trustee, for trustees
                </h2>
                <div className="space-y-6 font-serif text-lg text-warm-gray-600 leading-relaxed">
                  <p>
                    Huge amounts of Gift Aid go unclaimed by small UK charities
                    every year. The paperwork
                    is confusing, existing services charge monthly fees, and
                    HMRC&apos;s own tools aren&apos;t designed for volunteer
                    trustees fitting admin around a day job.
                  </p>
                  <p>
                    TrusteeKit exists to close that gap. Zero fixed costs — you
                    only pay when you claim Gift Aid. Everything from declarations
                    to HMRC submissions in one place.
                  </p>
                  <p>
                    It&apos;s also significantly cheaper than the alternatives.
                    But the point isn&apos;t being cheap — it&apos;s making
                    Gift Aid accessible to charities that would otherwise never
                    claim it.
                  </p>
                </div>
              </MotionSection>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section id="get-started" className="py-32 relative overflow-hidden">
          {/* Complex background */}
          <div className="absolute inset-0 bg-linear-to-br from-navy via-navy-light to-navy" />
          <div className="absolute inset-0 bg-mesh-hero opacity-50" />

          {/* Floating orbs */}
          <div className="absolute top-20 left-[10%] w-64 h-64 bg-trust-blue/20 rounded-full blur-3xl animate-pulse pointer-events-none" />
          <div className="absolute bottom-20 right-[10%] w-48 h-48 bg-growth-green/20 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDelay: '1s' }} />

          <div className="max-w-[1400px] mx-auto px-6 relative z-10 text-center">
            <MotionSection>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-xs rounded-full mb-8">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-growth-green opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-growth-green"></span>
                </span>
                <span className="text-sm font-medium text-white/80">Free to start</span>
              </div>

              <h2 className="font-display text-[clamp(2.5rem,5vw,4rem)] font-medium text-white leading-tight tracking-tight mb-6">
                Ready to get started?
              </h2>
              <p className="text-warm-gray-300 max-w-[480px] mx-auto mb-10 font-serif text-lg">
                Sign up in minutes. No card required, no monthly fee — only pay
                when you claim Gift Aid.
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  href="/signup"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-navy font-semibold rounded-xl hover:bg-warm-gray-100 transition-colors press-effect"
                >
                  Get started free
                </Link>
                <Link
                  href="/login"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors backdrop-blur-xs border border-white/20"
                >
                  Sign in
                </Link>
              </div>

              <p className="mt-6 text-sm text-warm-gray-500">No setup costs · No monthly subscription</p>
            </MotionSection>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
